<template>
  <div>
    <div v-if="!loading">
      <v-card>
        <v-card-text>
          <v-layout wrap>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                :value="order.orderId"
                outlined
                label="Order ID"
                dense
                readonly
              />
            </v-flex>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                v-model="order.poNum"
                outlined
                label="PO #"
                dense
                :readonly="!canCreateOrder"
              />
            </v-flex>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-select
                v-if="order.orderStatus !== 'Pending'"
                v-model="order.orderStatus"
                :value="order.orderStatus"
                :items="orderStatuses"
                :readonly="
                  order.orderStatus === 'Completed' || !canUpdateOrderStatus
                "
                label="Order Status"
                outlined
                dense
                @change="setUpdateOrderStatus"
              ></v-select>
              <v-text-field
                v-else
                v-model="order.orderStatus"
                label="Order Status"
                outlined
                dense
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                :value="order.company.name"
                outlined
                label="Customer"
                dense
                readonly
              />
            </v-flex>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                v-if="order.orderStatus !== 'Pending'"
                :value="order.shipTo.formatted_address"
                outlined
                label="Shipping Address"
                dense
                readonly
              />

              <v-autocomplete
                v-else
                :items="order.company.shippingAddresses"
                outlined
                dense
                label="Shipping Address"
                v-model="order.shipTo"
                item-text="formatted_address"
                item-value="_id"
                :readonly="!canCreateOrder"
                return-object
              >
                <template v-slot:item="data">
                  <address-list-item :address="data.item" />
                </template>
                <template v-slot:append-item>
                  <v-layout justify-center>
                    <v-btn
                      @click="initiateAddress('shippingAddresses')"
                      color="primary"
                      >Add Shipping Address</v-btn
                    >
                  </v-layout>
                </template>
              </v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                :value="order.orderDate"
                outlined
                label="Order Date"
                dense
                readonly
              />
            </v-flex>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                v-if="order.orderStatus !== 'Pending'"
                :value="order.billTo.formatted_address"
                outlined
                label="Billing Address"
                dense
                readonly
              />
              <v-autocomplete
                v-else
                :items="order.company.billingAddresses"
                outlined
                dense
                label="Billing Address"
                v-model="order.billTo"
                item-text="formatted_address"
                item-value="_id"
                return-object
                :readonly="!canCreateOrder"
              >
                <template v-slot:append-item>
                  <v-layout justify-center>
                    <v-btn
                      @click="initiateAddress('billingAddresses')"
                      color="primary"
                      >Add Billing Address</v-btn
                    >
                  </v-layout>
                </template>
                <template v-slot:item="data">
                  <address-list-item :address="data.item" />
                </template>
              </v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                :value="order.formattedOrderTotal"
                outlined
                label="Order Total"
                dense
                readonly
              />
            </v-flex>
            <v-flex sm12 md4 pl-2 pr-2>
              <v-text-field
                :value="order.specialInstructions"
                outlined
                label="Special instructions"
                dense
                readonly
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs12>
              <timeline title="Order Status" :timeline="order.timeline" />
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
      <v-layout mt-4>
        <v-flex>
          <v-alert icon="shopping_cart" color="primary" text>
            <v-flex style="float: left">Order Items</v-flex>
          </v-alert>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-alert
            v-if="pendingStatus && !validDistributors"
            type="error"
            dense
            >{{ selectDistributorMessage }}</v-alert
          >
          <v-data-table
            :headers="orderItemHeaders"
            :items="order.items"
            hide-default-footer
            :dense="true"
          >
            <template v-slot:item.orderStatus="{ item }">
              <v-select
                v-model="item.itemStatus"
                :items="orderStatuses"
                label="Order Status"
                outlined
                dense
                class="mt-6"
              ></v-select>
            </template>
            <template v-slot:item.distributor="{ item }">
              <v-autocomplete
                :error="pendingStatus && !validDistributors"
                :readonly="order.orderStatus !== 'Pending'"
                v-if="!order.orderedByAssociate && canSubmitOrder"
                :items="distributors"
                :dense="true"
                outlined
                label="Select Distributor"
                item-value="_id"
                class="mt-4"
                item-text="address.name"
                v-model="item.distributor"
              >
                <template v-slot:item="data">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.distributor.name }} |
                        {{ data.item.address.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>{{
                        data.item.address.formatted_address
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </template>
                <template slot="selection" slot-scope="data">
                  <template>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title
                          >{{ data.item.distributor.name }} |
                          {{ data.item.address.name }}</v-list-item-title
                        >
                        <v-list-item-subtitle>
                          {{ data.item.address.formatted_address }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </template>
              </v-autocomplete>
              <div v-else>{{ getDistributorName(item.distributor) }}</div>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
      <v-layout mt-4 v-if="pendingStatus">
        <v-flex xs12 text-sm-right>
          <v-btn
            @click="submitOrder"
            v-if="canSubmitOrder"
            :disabled="!validDistributors"
            color="primary"
            >Submit Order</v-btn
          >
        </v-flex>
      </v-layout>
      <update-status-modal
        v-if="updateStatusModal"
        :updateStatusModal.sync="updateStatusModal"
        :title="updateStatusTitle"
        :update-status-instructions="updateStatusInstructions"
        @updateStatusCtx="updateStatus"
      />
    </div>
    <v-layout v-else align-center justify-center>
      <v-progress-circular
        indeterminate
        color="primary"
        size="100"
      ></v-progress-circular>
    </v-layout>
    <address-modal
      v-if="addressModal"
      title="Add Address"
      :addressModal.sync="addressModal"
      :address="baseAddress"
      :distributors="distributors"
      @save="addAddress"
    />
  </div>
</template>

<script>
import request from "@/modules/request";
import UpdateStatusModal from "@/components/shared/modals/UpdateStatusModal";
import moment from "moment";
import { get, assign } from "lodash";
import Timeline from "@/components/shared/Timeline";
import { hasPermission } from "@/modules/rbac/permission-manifest";
import AddressListItem from "@/components/shared/AddressListItem";
import AddressModal from "@/components/shared/modals/AddressModal";
import CompanyService from "@/services/companies/company.service";

export default {
  name: "Order",
  inject: ["snackBar", "reactiveInjected"],
  async mounted() {
    await this.loadData();
    this.setOrderItemHeaders();
  },
  components: {
    UpdateStatusModal,
    Timeline,
    AddressListItem,
    AddressModal,
  },
  computed: {
    pendingStatus() {
      const status = get(this, "order.orderStatus");
      return status === "Pending";
    },
    validDistributors() {
      return this.order && this.order.items.every((elem) => elem.distributor);
    },
  },
  data() {
    return {
      companyService: new CompanyService(),
      addressModal: false,
      addressType: null,
      updateStatusModal: false,
      updateStatusTitle: "",
      updateStatusType: null,
      updateStatusInstructions: "",
      datepicker: true,
      order: null,
      distributors: [],
      loading: true,
      orderStatuses: ["Pending", "Submitted", "Confirmed", "Completed"],
      updatedStatus: null,
      orderItemHeaders: [
        {
          text: "Product Code",
          value: "productCode",
        },
        {
          text: "Category",
          value: "category.name",
        },
        {
          text: "Description",
          value: "description[0].value",
        },
        {
          text: "Quantity",
          value: "quantity",
        },
        {
          text: "Price",
          value: "formattedPrice",
        },
      ],
      selectDistributorMessage:
        "Please select a distributor for all order items before you can submit this order!",
      canSubmitOrder: hasPermission(
        "canSubmitOrder",
        null,
        this.reactiveInjected.activeUser
      ),
      canCreateOrder: hasPermission(
        "canCreateOrder",
        null,
        this.reactiveInjected.activeUser
      ),
      canUpdateOrderStatus: hasPermission(
        "canUpdateOrderStatus",
        null,
        this.reactiveInjected.activeUser
      ),
      baseAddress: null,
      categories: [],
    };
  },
  methods: {
    addAddress(address) {
      const { addressType } = this;
      const { company } = this.order;
      const companyId = company._id;
      const updatedAddresses =
        addressType === "shippingAddresses"
          ? {
              shippingAddresses: [...company.shippingAddresses, address],
            }
          : {
              billingAddresses: [...company.billingAddresses, address],
            };

      this.companyService
        .associateUpdateCompany(companyId, updatedAddresses)
        .then((results) => {
          const newAddresses = results.data[addressType];
          const latestAddress = newAddresses[newAddresses.length - 1];
          this.order.company[addressType] = newAddresses;
          this.addressType === "shippingAddresses"
            ? (this.order.shipTo = latestAddress)
            : (this.order.billTo = latestAddress);
          this.snackBar("Address added", "success");
        })
        .catch((error) => {
          console.error(`Error adding address: ${error}`);
          this.snackBar("Error adding address", "error");
        });

      this.addressModal = false;
    },
    initializeDistributorSettings() {
      const { categories } = this;
      return categories.map((category) => {
        return {
          category: category._id,
          name: category.name,
          distributor: null,
        };
      });
    },
    initiateAddress(addressType) {
      this.baseAddress = {
        formatted_address: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        meta: {},
      };

      if (addressType === "shippingAddresses") {
        this.baseAddress.meta.distributorSettings = this.initializeDistributorSettings();
        this.baseAddress.meta.emailSettings = [];
      }
      this.addressType = addressType;
      this.addressModal = true;
    },
    setOrderItemHeaders() {
      if (this.canSubmitOrder) {
        this.orderItemHeaders.push({
          text: "Distributor",
          value: "distributor",
          width: "10%",
        });
      }
    },
    async submitOrder() {
      const { _id, shipTo, billTo, poNum } = this.order;
      const payload = {
        _id,
        shipTo,
        billTo,
        poNum,
        orderItems: this.order.items.map((elem) => {
          const { _id, distributor } = elem;
          return {
            _id,
            distributor,
          };
        }),
      };
      await request
        .callInternal({
          method: "post",
          route: `/api/admin/order/submit`,
          data: payload,
        })
        .then(() => {
          this.$swal({
            title: "Congratulations!",
            text: `The order has been submitted.`,
            icon: "success",
          });
          this.loadData();
        })
        .catch((error) => {
          console.error(error);
          this.$swal({
            title: "Error",
            text: `Something went wrong with submitting this order. Please try again.`,
            icon: "error",
          });
          this.loadData();
        });
    },
    getDistributorName(distributor) {
      const distr = this.distributors.find((elem) => elem._id === distributor);
      return `${distr.distributor.name} | ${distr.address.name} | ${distr.address.formatted_address}`;
    },
    async updateStatus(ctx) {
      await request
        .callInternal({
          method: "patch",
          route: `/api/admin/orderstatus/${this.order._id}`,
          data: {
            ...ctx,
            orderStatus: this.order.orderStatus,
          },
        })
        .then(() => {
          this.$swal({
            title: "Success!",
            text: "Order status has been updated!",
            icon: "success",
          });
          this.loadData();
        })
        .catch((error) => {
          this.$swal({
            title: "Error",
            text:
              "Sorry, something went wrong with updating the order status. Please try again",
            icon: "error",
          });
        });
    },
    addressFormatter(address) {
      const { name, address1, address2, city, state, zip } = address;

      if (address2) {
        return `${name} - ${address1}, ${address2}, ${city}, ${state} ${zip}`;
      } else {
        return `${name} - ${address1}, ${city}, ${state} ${zip}`;
      }
    },
    async loadData() {
      const { getOrder, getDistributors, getCategories } = this;
      const [order, distributors, categories] = await Promise.all([
        getOrder(),
        getDistributors(),
        getCategories(),
      ])
        .then((results) => {
          return results;
        })
        .catch((error) => {
          console.error(error);
        });

      this.categories = categories;
      this.formatOrder(order);
      this.distributors = distributors;
      this.setDistributors();
      this.loading = false;
    },
    getCategories() {
      const opts = {
        method: "get",
      };
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/fullcategories",
        })
        .then((results) => {
          return results.data;
        });
    },
    formatOrder(order) {
      const formattedOrder = assign(order, {
        orderDate: moment(order.createdAt).format("MM-DD-YYYY"),
      });
      this.order = formattedOrder;
    },
    async getOrder() {
      const { id } = this.$route.params;
      return request
        .callInternal({
          method: "get",
          route: `/api/order/${id}`,
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async getDistributors() {
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/distributorlocations",
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    setUpdateOrderStatus() {
      this.updateStatusType = "order";
      this.updatedStatus = this.order.orderStatus;
      this.updateStatusInstructions =
        this.updatedStatus === "Completed"
          ? "Select order complete date"
          : "Select order ETA date";
      this.updateStatusModal = true;
    },
    setUpdateOrderItemStatus() {
      this.updateStatusType = "orderItem";
      this.updatedStatus = this.order.orderStatus;
      this.updateStatusModal = true;
    },
    setDistributors() {
      const distributorSettings = this.order.shipTo.meta.distributorSettings;
      this.order.items.forEach((item, index) => {
        if (!item.distributor) {
          const category = item.category._id;
          const setting = distributorSettings.find(
            (elem) => elem.category === category
          );
          if (setting && setting.distributor) {
            this.order.items[index].distributor = setting.distributor;
          }
        }
      });
    },
  },
};
</script>

<style scoped>
/* TODO - combine this css style */
.v-data-table >>> .v-data-table__wrapper td,
.v-data-table >>> .v-data-table__wrapper th {
  padding: 5px;
}

@media all and (max-width: 640px) {
  .v-data-table >>> .v-data-table__wrapper table,
  .v-data-table >>> .v-data-table__wrapper thead,
  .v-data-table >>> .v-data-table__wrapper tbody,
  .v-data-table >>> .v-data-table__wrapper th,
  .v-data-table >>> .v-data-table__wrapper td,
  .v-data-table >>> .v-data-table__wrapper tr {
    display: block;
  }
  .v-data-table >>> .v-data-table__wrapper td {
    height: auto;
    border: none !important;
  }

  .v-data-table >>> .v-data-table__wrapper tr {
    padding: 5px;
  }

  .v-data-table >>> .v-data-table__wrapper .v-data-table__mobile-row__cell {
    text-align: left !important;
  }

  .v-data-table >>> .v-data-table__wrapper tr:nth-of-type(even) {
    background: #eee;
  }

  .v-data-table >>> .v-data-table__wrapper th {
    margin-bottom: 15px;
    border: none !important;
  }
}
</style>
